import Grid from '@mui/material/GridLegacy';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import StyledComponent from 'styled-components';

import ApiRequest from '../../api/request';
import DataModifier from '../../utils/DataModifier';
import { AlertContext, ApplySelectionButton, EnhancedTableRef, FormInputChange, FormInputType, FormInputTypeEnum, FormLoader, FormValidated, FormValidatedRef, Table } from '@bit-by-bit-software/ui';
import { CookieValues, ModelNameEnum } from '@bit-by-bit-software/types';

const TABLE_HEADERS = [
  {
    id: 'type',
    filter: true,
    label: 'Type',
  },
  {
    id: 'description',
    filter: true,
    label: 'Description',
  },
  {
    id: 'data.model',
    filter: true,
    label: 'Model',
  },
  {
    id: 'data.schemaField',
    filter: false,
    label: 'Schema Field',
  },
  {
    id: 'data.changes',
    filter: false,
    label: 'Changes',
  },
];
/*
const SCHEMA_FIELDS = [
  {
    autoPopulate: false,
    isArray: false,
    keyInReference: '',
    label: 'Type',
    lowercase: false,
    maxLength: 128,
    minLength: 0,
    name: 'type',
    trim: true,
    type: 'string',
    unique: false,
  },
  {
    autoPopulate: false,
    isArray: false,
    keyInReference: '',
    label: 'Description',
    lowercase: false,
    maxLength: 128,
    minLength: 0,
    name: 'description',
    trim: true,
    type: 'string',
    unique: false,
  },
  {
    autoPopulate: false,
    isArray: false,
    keyInReference: '',
    label: 'Model',
    lowercase: false,
    maxLength: 128,
    minLength: 0,
    name: 'model',
    trim: true,
    type: 'string',
    unique: false,
  },
  {
    autoPopulate: false,
    isArray: false,
    keyInReference: '',
    label: 'Schema field',
    lowercase: false,
    maxLength: 128,
    minLength: 0,
    name: 'schemaField',
    trim: false,
    type: 'object',
    unique: false,
  },
  {
    autoPopulate: false,
    isArray: true,
    keyInReference: '',
    label: 'Changes',
    lowercase: false,
    maxLength: 128,
    minLength: 0,
    name: 'changes',
    trim: false,
    type: 'object',
    unique: false,
  },
];
*/
const REFRESH_QA_BACKUP = true;

const Container = StyledComponent.div`
			.bottom-btn-section
			{
				margin-bottom: 0px;
				margin-top: 50px;
				justify-content: space-between;
		    display: flex;
			}
			.schema-form
			{
				margin-top: 15px;
			}
			.MuiChip-outlinedPrimary
			{
				width: 100%;
				margin-top: 10px;
			}
			.data-manager-large-table: {
				width: 100%;
			}
		`;

function SchemaManager() {
  const [cookies] = useCookies<'token', CookieValues>(['token']);

  const { showAlert } = useContext(AlertContext);

  const tableRef = useRef<EnhancedTableRef>(null);
  const schemaFormRef = useRef<FormValidatedRef>(null);
  const [differences, setDifferences] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getDifferences = async (evt: any) => {
    evt.preventDefault();

    setIsLoading(true);

    try {
      const inputKeys = stepOneFormInputs.map(input => input.id);
      const backendUrlIdx = inputKeys.indexOf('backendUrl');
      const secretKeyIdx = inputKeys.indexOf('secretKey');

      const params = {
        secretKey: stepOneFormInputs[secretKeyIdx].value,
        skipDocuments: true,
      };
      // Tell DV to initiate backup
      // @todo make this function something that can be controlled via checkbox on page
      if (REFRESH_QA_BACKUP) {
        const backupResults = await ApiRequest.sendExternalRequest({
          url: stepOneFormInputs[backendUrlIdx].value,
          method: 'post',
          params,
          path: 'model/backup',
          token: cookies.token,
        });

        if (backupResults.data.error) {
          showAlert({
            headerText: 'Uh-oh',
            detailText: backupResults.data.error,
            variant: 'danger',
            dismissText: 'okay',
          });
          return;
        }
      }

      // Tell current ENV to read back up and give us a list of differences
      const differencesResults = await ApiRequest.sendRequest({
        method: 'post',
        params: {},
        path: 'model/differences',
        token: cookies.token,
      });

      if (differencesResults.data.error) {
        console.error(differencesResults.data.error);
        throw new Error(differencesResults.data.error);
      }

      setDifferences(differencesResults.data.results);
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
        variant: 'danger',
        dismissText: 'okay',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const [stepOneFormInputs, setStepOnFormInputs] = useState<FormInputType[]>([
    {
      label: 'Backend URL',
      id: 'backendUrl',
      element: 'input',
      type: FormInputTypeEnum.text,
      validation: {
        requirement: 'A backend URL is required',
        algorithm: 'range',
        min: 5,
        max: 128,
      },
      required: 'required',
      placeholder: 'http://backend.com/',
      value: process.env.REACT_APP_API_SERVER_URL,
      class: '',
      row: 0,
      col: 0,
    },
    {
      label: 'Secret Key',
      id: 'secretKey',
      element: 'input',
      type: FormInputTypeEnum.text,
      validation: { requirement: 'A secret key is required', algorithm: 'range', min: 5, max: 128 },
      required: 'required',
      placeholder: 's3cr3t',
      value: '',
      class: '',
      row: 0,
      col: 0,
    },
    {
      label: '',
      id: 'submitBtn',
      element: 'input',
      type: FormInputTypeEnum.submit,
      value: 'Find Differences',
      onClick: getDifferences,
      class: 'btn-block  .border .border--white',
      placeholder: '',
      row: 0,
      col: 0,
      required: '',
    },
  ]);

  const applyDifferences = async ({ differenceIds }: { differenceIds: string[] }) => {
    setIsLoading(true);
    const differencesToApply = differences.filter(
      difference => differenceIds.indexOf(difference._id) !== -1,
    );

    const params = {
      differences: differencesToApply,
    };
    try {
      const response = await ApiRequest.sendRequest({
        method: 'post',
        params,
        path: 'model/apply-differences',
        token: cookies.token,
      });
      if (response.data.error) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: response.data.error,
          variant: 'danger',
          dismissText: 'okay',
        });
        return;
      }

      const updatedDifferences = DataModifier.filterObjectsOutOfLhs({
        lhs: [...differences],
        rhs: differencesToApply,
        key: '_id',
      });
      setDifferences(updatedDifferences);
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
        variant: 'danger',
        dismissText: 'okay',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const schemaFormOnChange = (change: FormInputChange, _: boolean) => {
    const formInputs = [...stepOneFormInputs];
    for (let i = 0; i < formInputs.length; i++) {
      if (formInputs[i].id === change.id) {
        formInputs[i].value = change.value;
        break;
      }
    }
    setStepOnFormInputs(formInputs);
  };

  const tableDidStartLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  const tableDidFinishLoading = useCallback(
    (action: any, message: string | null) => {
      try {
        const modifiedData = tableRef.current?.handleAction(action, differences);
        if (modifiedData) {
          setDifferences(modifiedData);
        }
        showAlert({
          headerText: 'Success',
          detailText: message ?? '',
          variant: 'success',
          dismissText: 'okay',
        });
      } catch (err: any) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: `An error has occurred, please try again or contact support.\nError: ${err.emssage}`,
          variant: 'danger',
          dismissText: 'okay',
          err,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [differences, showAlert],
  );

  return (
    <Container>
      <FormLoader isLoading={isLoading} />
      <Grid container spacing={1}>
        {/* Schema field form */}
        <Grid item xs={7} className="schema-form">
          <FormValidated
            ref={schemaFormRef}
            formInputs={stepOneFormInputs}
            formOnChange={schemaFormOnChange}
            dynamicInputs
            validateOnInit={false}
            columns={2}
            description=""
          />
        </Grid>

        {differences.length > 0 && (
          <div className="data-manager-large-table" style={{ marginBottom: '25px', width: '100%' }}>
            <Table
              ref={tableRef}
              data={differences}
              headers={TABLE_HEADERS}
              model={ModelNameEnum.model}
              //  schemaFields={SCHEMA_FIELDS}
              selectAllEnabled
              multiSelectEnabled
              defaultSort="model"
              sendRequest={ApiRequest.sendRequest}
              sortEnabled
              tableDidStartLoading={tableDidStartLoading}
              tableDidFinishLoading={tableDidFinishLoading}
              isDeleteAvailable={false}
              isUpdateAvailable={false}
              updateFormInputs={[]}
              createFormInputs={[]}
              isCreateAvailable={false}
              isCsvAvailable
              customButton1={({ data }: { data: any[] }) => (
                <ApplySelectionButton
                  onClick={() => {
                    applyDifferences({ differenceIds: data });
                  }}
                />
              )}
              onUpdateFormInputsChanged={() => {}}
              onCreateFormInputsChanged={() => {}}
              token={cookies.token}
            />
          </div>
        )}
      </Grid>
    </Container>
  );
}

export default SchemaManager;
